import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleOutline } from '@iwoca/icons';
import { Label, Link, Pill, TextArea } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyQuestionAI.module.css';
import { useCompanyQuestionMutation } from '../../api/apiCompanyQuestion/apiCompanyQuestionMutation';
import { useGetCompanyQuestion } from '../../api/apiCompanyQuestion/apiCompanyQuestionQuery';
import {
  TCompanyQuestionRequest,
  TSicCode,
} from '../../api/apiCompanyQuestion/fetchPostCompanyQuestion';
import {
  useGetApplicationQuery,
  useGetStateQuery,
  useGetUserConsent,
} from '../../api/lendingApi/lendingApiQueries';
import { useUserConsentMutation } from '../../api/userConsentApi/useUserConsentMutation';
import { useOnce } from '../../hooks/useOnce';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { TGetStateResponse } from '../../types';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import logger from '../../utils/logger';
import { Button } from '../Button/Button';
import RouteSpinner from '../RouteSpinner/RouteSpinner';

type TCompanyQuestionAIData = {
  answer: string;
  sicCode: string[];
};

type TQuestionHistory = {
  id: number;
  question: string;
  answer: string;
};

type TSessionData = {
  questions: TQuestionHistory[];
  nextQuestion: string;
};

const getSessionStorageKey = (applicationId: string) =>
  `company_questions_${applicationId}`;

const getSessionData = (applicationId?: string): TSessionData => {
  if (!applicationId) return { questions: [], nextQuestion: '' };

  try {
    const data = sessionStorage.getItem(getSessionStorageKey(applicationId));
    return data ? JSON.parse(data) : { questions: [], nextQuestion: '' };
  } catch {
    return { questions: [], nextQuestion: '' };
  }
};

const getSicCodesSessionData = (companyHouseNumber: string): TSicCode => {
  try {
    const data = sessionStorage.getItem(
      `sic_codes_for_company_${companyHouseNumber}`,
    );

    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const setSessionData = (applicationId: string, data: TSessionData): void => {
  sessionStorage.setItem(
    getSessionStorageKey(applicationId),
    JSON.stringify(data),
  );
};

const normaliseCompanyQuestion = (
  history: TQuestionHistory[],
  sicCodes?: string[],
  incorporationDate?: string,
): TCompanyQuestionRequest => {
  return {
    context: {
      sic_code: sicCodes || [],
      incorporation_date: incorporationDate || '',
    },
    history,
  };
};

export const COMPANY_QUESTION_CLOSE_SESSION = 'company_question_close_session';
export const USER_CONSENT_PROVIDER = 'signup_ai_company_questions';

const CompanyQuestionAI = () => {
  const maxTextLength = 500;

  const [textCount, setTextCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [sicCodes, setSicCodes] = useState<string[]>([]);
  const [sessionStorageLoaded, setSessionStorageLoaded] = useState(false);

  const { application } = useGetApplicationQuery();
  const [questionStorage, setQuestionStorage] = useState<TSessionData>(
    getSessionData(application?.application_id),
  );

  const { postCompanyQuestion, isLoading } = useCompanyQuestionMutation();

  const { state } = useGetStateQuery<TGetStateResponse>();
  const {
    initialQuestion,
    isInitialCompanyQuestionLoading,
    isInitialCompanyQuestionError,
  } = useGetCompanyQuestion({
    contextData: {
      sic_code: sicCodes,
      incorporation_date: state?.application?.company?.trading_from_date,
    },
    enabled: sessionStorageLoaded && questionStorage?.questions.length === 0,
  });

  const { navigateSignup } = useSignupRouterUK();

  const methods = useForm<TCompanyQuestionAIData>();
  const { handleSubmit, register, reset } = methods;

  const { userConsent } = useGetUserConsent(USER_CONSENT_PROVIDER);

  const { postUserConsent } = useUserConsentMutation();
  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Company Question AI',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  useOnce({
    fn: () => {
      if (!application?.application_id) return;

      const sessionData = getSessionData(application.application_id);

      if (!state.application?.company?.company_number) return;

      const sessionSicCodes = getSicCodesSessionData(
        state.application?.company?.company_number,
      );
      const closeSessionStatus = Boolean(
        sessionStorage.getItem(COMPANY_QUESTION_CLOSE_SESSION),
      );

      setSicCodes(sessionSicCodes || []);
      setQuestionStorage(sessionData);

      setShowThankYou(closeSessionStatus);

      setSessionStorageLoaded(Boolean(sessionData));

      if (sessionData.questions.length >= 5) {
        setShowThankYou(true);
      }
    },
    conditionFn: Boolean(application?.application_id),
  });

  useEffect(() => {
    if (!sessionStorageLoaded) return;

    if (questionStorage.nextQuestion) {
      return setCurrentQuestion(questionStorage.nextQuestion);
    }

    if (initialQuestion && questionStorage.questions.length === 0) {
      return setCurrentQuestion(initialQuestion);
    }

    if (
      !isInitialCompanyQuestionLoading ||
      isInitialCompanyQuestionError ||
      questionStorage.questions.length === 0
    ) {
      setCurrentQuestion(
        'What products or services does your business sell, and who are your typical customers?',
      );
    }
  }, [
    initialQuestion,
    isInitialCompanyQuestionLoading,
    isInitialCompanyQuestionError,
    questionStorage,
    sessionStorageLoaded,
  ]);

  const handleManageUserConsent = async () => {
    if (!userConsent) {
      await postUserConsent({
        body: {
          data: {
            bundle_hash: viteBuildHash,
          },
        },
        provider: USER_CONSENT_PROVIDER,
        stateKey: state?.state_key || '',
      });
    }
  };

  const onSubmit = async (data: TCompanyQuestionAIData) => {
    if (!application?.application_id) {
      return;
    }

    const updatedQuestionCount = questionStorage.questions.length + 1;

    const newQuestionEntry: TQuestionHistory = {
      id: updatedQuestionCount,
      question: currentQuestion,
      answer: data.answer,
    };

    const updatedHistory = [...questionStorage.questions, newQuestionEntry];

    try {
      const response = await postCompanyQuestion({
        applicationId: application.application_id,
        data: normaliseCompanyQuestion(
          updatedHistory,
          sicCodes,
          state.application?.company?.trading_from_date,
        ),
      });

      tracking.ampli.companyQuestionSubmitted(
        getAmplitudeDefaultEventProperties(),
      );

      setSessionData(application.application_id, {
        questions: updatedHistory,
        nextQuestion: response.inference_question || '',
      });

      setQuestionStorage({
        questions: updatedHistory,
        nextQuestion: response.inference_question || '',
      });

      const shouldShowThankYou =
        response.questions_complete || updatedQuestionCount >= 5;

      if (shouldShowThankYou) {
        setShowThankYou(true);
      } else if (response.inference_question) {
        setCurrentQuestion(response.inference_question);
        reset();
        setTextCount(0);
      }

      await handleManageUserConsent();
    } catch (e) {
      setSessionData(application.application_id, {
        questions: updatedHistory,
        nextQuestion: '',
      });

      setQuestionStorage({
        questions: updatedHistory,
        nextQuestion: '',
      });

      // TODO: BE to handle 5xx error instead of FE allowing customers to fail silently and continue with the customer journey
      setShowThankYou(true);

      sessionStorage.setItem(COMPANY_QUESTION_CLOSE_SESSION, 'true');
      logger.error('Failed to set ai question', {
        error: e,
      });
    }
  };

  if (isInitialCompanyQuestionLoading && !sessionStorageLoaded) {
    return <RouteSpinner />;
  }

  const shouldShowAIUI = !isInitialCompanyQuestionError;
  return (
    <>
      <div className={styles.container}>
        {shouldShowAIUI && <Pill>Powered by AI</Pill>}
        <h2 className={styles.heading}>
          Tell us a bit more about your business
        </h2>
        <p className={styles.description}>
          This will help us understand your business better and more accurately
          assess your application.
        </p>
        {shouldShowAIUI && (
          <>
            <div className={styles.info}>
              <IconInfoCircleOutline
                data-testid="infoIcon"
                width={16}
                height={16}
                className={styles.infoIcon}
              />
              <p className={styles.infoDescription}>
                There are a maximum of five AI generated questions and they
                should take around two minutes to complete.
              </p>
            </div>
          </>
        )}

        {questionStorage.questions.length > 0 && (
          <div className={styles.previousQuestions}>
            {questionStorage.questions.map((history) => (
              <div key={history.id} className={styles.previousQuestion}>
                <p className={styles.previousQuestion}>{history.question}</p>
                <p className={styles.previousAnswer}>{history.answer}</p>
              </div>
            ))}
          </div>
        )}

        {!showThankYou && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
              <div className={styles.formFieldContainer}>
                <div className={styles.formField}>
                  <Label htmlFor="questionAndAnswer">{currentQuestion}</Label>
                  <TextArea
                    data-testid={'answer-textarea'}
                    id="questionAndAnswer"
                    placeholder="Type your answer here..."
                    maxLength={maxTextLength}
                    className={styles.questionTextArea}
                    {...register('answer', { required: true })}
                    onChange={(e) => setTextCount(e.currentTarget.value.length)}
                  />
                </div>
                <p
                  data-testid="character-count"
                  className={styles.characterCount}
                >
                  {textCount} / {maxTextLength} characters
                </p>
              </div>
              <Button
                className={styles.nextQuestionBtn}
                type="submit"
                disabled={isLoading}
                loading={isLoading}
              >
                Continue
              </Button>
              {shouldShowAIUI && questionStorage.questions.length < 1 && (
                <p
                  data-testid="ai-privacy-policy"
                  className={styles.privacyPolicy}
                >
                  By clicking “Continue” you agree to our{' '}
                  <Link
                    href="https://www.iwoca.co.uk/compliance/customer-privacy-policy"
                    className={styles.privacyPolicyLink}
                  >
                    privacy policy
                  </Link>
                  .
                </p>
              )}
            </form>
          </FormProvider>
        )}
        {showThankYou && (
          <>
            <p className={styles.thankYouMessage}>
              Thanks for that information. You can now continue to the next step
              of your application.
            </p>

            <Button
              className={styles.nextQuestionBtn}
              onClick={() => {
                tracking.ampli.companyQuestionsCompleted(
                  getAmplitudeDefaultEventProperties(),
                );
                navigateSignup({ route: 'check-eligibility' });
              }}
            >
              Continue to check eligibility
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export default CompanyQuestionAI;
